/**
 * `useUpdateFolder` mutation hook.
 *
 * Wraps the raw `updateProjectFolder` service fn. Cache
 * invalidation: invalidates the specific folder detail key + any folder
 * listings under the project so renamed/updated folders refresh in tables.
 */
import type {
  FolderResponseDto,
  UpdateFolderDto,
} from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BRIEFS_QUERY_KEYS } from '@/entities/brief';
import { PROJECTS_QUERY_KEYS } from '@/entities/project';
import { PROJECT_TAG_QUERY_KEYS } from '@/entities/project-tag';
import { updateProjectFolder } from './project-folders.api';

export { updateProjectFolder };

interface UpdateFolderVariables {
  folderId: string;
  data: UpdateFolderDto;
}

interface UseUpdateFolderOptions {
  projectId: string;
  /**
   * Called after the cache work, so a consumer reading the cache sees the
   * write. Hook-level rather than per-call on purpose: TanStack drops the
   * callbacks handed to `mutate` once their observer loses its listeners
   * (`MutationObserver` checks `hasListeners()`), and a surface drawn by the
   * folder it is writing to can unmount before the request lands.
   */
  onSuccess?: (folder: FolderResponseDto, variables: UpdateFolderVariables) => void;
  /** Same reason: the caller's report of a refusal has to outlive its pane. */
  onError?: (error: Error, variables: UpdateFolderVariables) => void;
}

export function useUpdateFolder(options: UseUpdateFolderOptions) {
  const queryClient = useQueryClient();
  const { projectId, onSuccess, onError } = options;

  return useMutation<FolderResponseDto, Error, UpdateFolderVariables>({
    mutationFn: ({ folderId, data }) => updateProjectFolder(projectId, folderId, data),
    onError,
    onSuccess: (folder, variables) => {
      const { folderId, data } = variables;
      void queryClient.invalidateQueries({
        queryKey: PROJECTS_QUERY_KEYS.folders.detail(projectId, folderId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['project', projectId, 'folders'],
      });
      // Tags may have been minted server-side via findOrCreateByNames; refresh
      // the workspace-wide tag pool so suggestions stay current across surfaces.
      if (data.tags !== undefined) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_TAG_QUERY_KEYS.all });
      }
      // The link is one edge read from both ends: the briefs list reports the
      // folders each brief is delivered into, so writing it here leaves that
      // side of the cache describing a link that no longer exists.
      if (data.briefId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: BRIEFS_QUERY_KEYS.all });
      }

      onSuccess?.(folder, variables);
    },
  });
}
